import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { FaSearch } from "react-icons/fa";
import { getUserTimeEntriesByUserId } from "../../Services/timeEntryService";

const getWeekStartAndEnd = (date) => {
  const d = new Date(date);
  const day = d.getDay();

  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return { weekStart: monday, weekEnd: sunday };
};

const formatDate = (date) => {
  const options = { month: "short", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

const formatDateWithYear = (date) => {
  const options = { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

const formatAPIDateToYYYYMMDD = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

const SearchIcon = () => (
  <span
    style={{
      display: "inline-flex",
      width: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    🔍
  </span>
);

const ChevronLeftIcon = () => (
  <span
    style={{
      display: "inline-flex",
      width: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    ◀
  </span>
);

const ChevronRightIcon = () => (
  <span
    style={{
      display: "inline-flex",
      width: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    ▶
  </span>
);

const DownloadIcon = () => (
  <span
    style={{
      display: "inline-flex",
      width: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
      marginRight: 8,
    }}
  >
    ⬇️
  </span>
);

export default function UserWeeklyOverview() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [userId, setUserId] = useState("67c93124c0b873061f111797");

  const { weekStart, weekEnd } = getWeekStartAndEnd(currentDate);
  const weekRange = `${formatDate(weekStart)} - ${formatDateWithYear(weekEnd)}`;

  const goToPreviousWeek = useCallback(() => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  }, [currentDate]);

  const goToNextWeek = useCallback(() => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  }, [currentDate]);

  const handleProjectChange = useCallback((e) => {
    setSelectedProject(e.target.value);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const exportWeeklyReport = useCallback(() => {
    alert("Exporting weekly report...");
  }, []);

  const transformAPIData = (apiData) => {
    if (!Array.isArray(apiData)) {
      apiData = [apiData];
    }

    return apiData.map((entry) => ({
      id: entry._id,
      userId: entry.userId._id,
      date: formatAPIDateToYYYYMMDD(entry.date),
      projectId: entry.projectId._id,
      projectName: entry.projectId.projectName,
      scaffoldId: entry.scaffoldId ? entry.scaffoldId.scaffoldId : "N/A",
      startTime: entry.startTime,
      endTime: entry.endTime,
      breakTime: 30,
      totalHours: entry.totalHours,
      workType: entry.workTypeId ? entry.workTypeId.name : "Regular",
      additionalCompensation: entry.compensationTypeId
        ? entry.compensationTypeId.name
        : null,
      status: entry.approvalStatus === "pending" ? "Pending" : "Approved",
      comments: entry.comments,
    }));
  };

  const extractProjects = (logs) => {
    const uniqueProjects = {};

    logs.forEach((log) => {
      if (!uniqueProjects[log.projectId]) {
        uniqueProjects[log.projectId] = {
          id: log.projectId,
          name: log.projectName,
        };
      }
    });
    return Object.values(uniqueProjects);
  };

  useEffect(() => {
    const fetchUserTimeEntries = async () => {
      setLoading(true);
      try {
        const startDateString = weekStart.toISOString().split("T")[0];
        const endDateString = weekEnd.toISOString().split("T")[0];

        const response = await getUserTimeEntriesByUserId(userId);

        if (response && response.data) {

          const transformedData = transformAPIData(response.data);

          const weekStartDate = new Date(startDateString);
          const weekEndDate = new Date(endDateString);

          let dateFilteredLogs = transformedData.filter((log) => {
            const logDate = new Date(log.date);
            return logDate >= weekStartDate && logDate <= weekEndDate;
          });

          const projectsFromLogs = extractProjects(transformedData);
          setUserProjects(projectsFromLogs);

          if (selectedProject) {
            dateFilteredLogs = dateFilteredLogs.filter(
              (log) => log.projectId === selectedProject
            );
          }

          setTimeLogs(dateFilteredLogs);
          setError(null);
        } else {
          throw new Error("Failed to fetch time entries");
        }
      } catch (err) {
        setError("Failed to load time logs. Please try again later.");
        console.error("Error fetching time logs:", err);
        setTimeLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserTimeEntries();
  }, [userId, selectedProject, weekStart.getTime(), weekEnd.getTime()]);

  const handleProjectClick = useCallback(
    (projectId) => {
      setSelectedProject(projectId === selectedProject ? "" : projectId);
    },
    [selectedProject]
  );

  const filteredLogs = timeLogs.filter((log) => {
    if (
      searchTerm &&
      !log.scaffoldId.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;
    return true;
  });

  const totalHours = filteredLogs
    .reduce((sum, log) => sum + log.totalHours, 0)
    .toFixed(1);
  const overtimeHours = filteredLogs
    .filter(
      (log) =>
        log.workType.toLowerCase().includes("overtime") ||
        log.workType.toLowerCase().includes("additional")
    )
    .reduce((sum, log) => sum + log.totalHours, 0)
    .toFixed(1);
  const bonusCount = filteredLogs.filter(
    (log) => log.additionalCompensation
  ).length;

  return (
    <div className="min-h-[90vh]">
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardHeader
              title="My Projects"
              titleTypographyProps={{ variant: "h6" }}
            />
            <Divider />

            {loading && userProjects.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 4,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : error && userProjects.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            ) : userProjects.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="info">No projects found</Alert>
              </Box>
            ) : (
              <>
                <CardContent sx={{ p: 2 }}>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="project-select-label">
                      Filter by Project
                    </InputLabel>
                    <Select
                      labelId="project-select-label"
                      value={selectedProject}
                      label="Filter by Project"
                      onChange={handleProjectChange}
                    >
                      <MenuItem value="">All Projects</MenuItem>
                      {userProjects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <div style={{ position: "relative", width: "100%" }}>
                    <input
                      type="text"
                      placeholder="Search scaffolds..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{
                        width: "100%",
                        padding: "8px 12px 8px 36px",
                        fontSize: "16px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        outline: "none",
                      }}
                    />
                    <FaSearch
                      size={16}
                      style={{
                        position: "absolute",
                        left: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#999",
                      }}
                    />
                  </div>
                </CardContent>

                <Divider />

                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Project Summary
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {userProjects.length} active projects
                    </Typography>

                    {userProjects.map((project) => {
                      const projectHours = timeLogs
                        .filter((log) => log.projectId === project.id)
                        .reduce((sum, log) => sum + log.totalHours, 0);

                      return (
                        <Card
                          key={project.id}
                          variant="outlined"
                          sx={{
                            mb: 1,
                            p: 1.5,
                            backgroundColor:
                              selectedProject === project.id
                                ? "action.selected"
                                : "inherit",
                            cursor: "pointer",
                          }}
                          onClick={() => handleProjectClick(project.id)}
                        >
                          <Typography variant="subtitle2">
                            {project.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {projectHours.toFixed(1)} hours this week
                          </Typography>
                        </Card>
                      );
                    })}
                  </Box>
                </CardContent>
              </>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          <Card>
            <CardHeader
              title={
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                    zIndex: 1,
                  }}
                >
                  <Typography variant="h5">
                    My Weekly Time Log
                    {selectedProject &&
                      userProjects.find((p) => p.id === selectedProject) &&
                      ` - ${
                        userProjects.find((p) => p.id === selectedProject).name
                      }`}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, zIndex: 1, }}>
                    <IconButton size="small" onClick={goToPreviousWeek}>
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                      {weekRange}
                    </Typography>
                    <IconButton size="small" onClick={goToNextWeek}>
                      <ChevronRightIcon />
                    </IconButton>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={exportWeeklyReport}
                      size="small"
                    >
                      Export Report
                    </Button>
                  </Box>
                </Box>
              }
              disableTypography
            />
            <Divider />
            <CardContent sx={{ zIndex: 99999 }}>
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 4,
                    zIndex: 99999
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2, zIndex: 99999 }}>
                  {error}
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ zIndex: 99999 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell>Scaffold ID</TableCell>
                        <TableCell>Total Hours</TableCell>
                        <TableCell>Work Type</TableCell>
                        <TableCell>Compensation</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">
                              No time logs found for this week
                              {searchTerm ? " matching your search" : ""}
                              {selectedProject
                                ? " in the selected project"
                                : ""}
                              .
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>{log.date}</TableCell>
                            <TableCell>{log.projectName}</TableCell>
                            <TableCell>{log.scaffoldId}</TableCell>
                            <TableCell>{log.totalHours}</TableCell>
                            <TableCell>{log.workType}</TableCell>
                            <TableCell>
                              {log.additionalCompensation || "None"}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={log.status}
                                color={
                                  log.status === "Approved"
                                    ? "success"
                                    : "default"
                                }
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!loading && !error && filteredLogs.length > 0 && (
                <Grid container spacing={3} sx={{ mt: 2, zIndex: 99999 }}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ zIndex: 9999 }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                          {totalHours}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Hours
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}
